/**
 * DecisionFlow — TenantDetail
 * ui/src/components/platform-admin/TenantDetail.jsx
 * OI-SEAT-IMPLEMENTATION · S660
 * OI-PLAN-BUNDLE-ADMIN-UI · S669
 * OI-CONNECTOR-ADMIN-UI · S669
 *
 * Full tenant detail page. Route: /platform-admin/tenants/:tenantId
 * Fetches GET /api/v1/admin/tenants/{tenant_id}
 *
 * Response shape (per tenant_admin_router.py GET /admin/tenants/{id}):
 *   {
 *     tenant_id, tenant_name, tenant_slug, subscription_tier,
 *     tenant_status_code, is_demo, hq_country_code, reporting_currency_code,
 *     legal_entity_name, billing_email, ein (masked), vat_number,
 *     duns_number, po_number, msa_signed_at, msa_reference,
 *     dpa_signed_at, dpa_reference,
 *     max_positions, active_position_count, utilisation_pct,
 *     billing_history: [...],
 *     platform_events: [...],
 *   }
 *
 * Seat adjustment via PATCH /api/v1/admin/tenants/{id}/seats
 *   body: { new_max_positions: int, reason: str }
 *
 * Plan bundle via GET/PATCH /api/v1/admin/tenants/{id}/plan-bundle
 *   GET 200: { bundle_name, plan_keys: [...], end_date, assignment_reason }
 *   GET 404: no bundle assigned — all 28 plan types open (short-circuit ALLOW)
 *   PATCH body: { plan_keys: [...], end_date: str|null, assignment_reason: str }
 *
 * Connectors via GET/PATCH /api/v1/admin/tenants/{id}/connectors
 *   GET 200: { bundle_name, connector_keys: [...], end_date, assignment_reason }
 *   GET 404: no connector bundle assigned
 *   PATCH body: { connector_keys: [...], end_date: str|null, assignment_reason: str }
 *
 * Design rules (§15):
 *   - No dark: prefix
 *   - rgb(var(--color-*)) throughout
 *   - .skeleton for loading states
 */

import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft, Edit2, Check, X, AlertTriangle, RefreshCw, Package, Plug, FileText } from "lucide-react";

import Card from "../shared/Card.jsx";
import Button from "../shared/Button.jsx";

const API_BASE = "/api/v1";

// ---------------------------------------------------------------------------
// Plan type catalog — 28 keys (§3.2 spec_feature_gate_s665)
// ---------------------------------------------------------------------------

const ALL_PLAN_KEYS = [
  { key: "feature.plan.transaction",      label: "Transaction-Level Crediting" },
  { key: "feature.plan.draw",             label: "Draw / Guarantee" },
  { key: "feature.plan.clawback",         label: "Clawback" },
  { key: "feature.plan.ramp",             label: "Ramp Schedules" },
  { key: "feature.plan.spiff",            label: "SPIFF / Tactical Incentives" },
  { key: "feature.plan.splits",           label: "Deal Splits" },
  { key: "feature.plan.overlay",          label: "Overlay Engine" },
  { key: "feature.plan.territory",        label: "Territory Compensation" },
  { key: "feature.plan.mbo",              label: "MBO Component" },
  { key: "feature.plan.nps",              label: "NPS Component" },
  { key: "feature.plan.adoption",         label: "Adoption Component" },
  { key: "feature.plan.kicker",           label: "Kicker Plans" },
  { key: "feature.plan.cosell",           label: "Co-Sell" },
  { key: "feature.plan.contest",          label: "Contests" },
  { key: "feature.plan.bonus_pool",       label: "Bonus Pools" },
  { key: "feature.plan.non_cash",         label: "Non-Cash Awards" },
  { key: "feature.plan.pod",              label: "Pod Compensation" },
  { key: "feature.plan.multi_year",       label: "Multi-Year Deals" },
  { key: "feature.plan.volume",           label: "Volume-Based Plans" },
  { key: "feature.plan.margin",           label: "Margin Compensation" },
  { key: "feature.plan.market_share",     label: "Market Share Plans" },
  { key: "feature.plan.ranking",          label: "Ranking Plans" },
  { key: "feature.plan.pharma",           label: "Pharma Script Plans" },
  { key: "feature.plan.channel",          label: "Channel Compensation" },
  { key: "feature.plan.true_up",          label: "True-Up Plans" },
  { key: "feature.plan.retention",        label: "Retention Plans" },
  { key: "feature.plan.plan_versioning",  label: "Plan Versioning" },
  { key: "feature.plan.grr_nrr",          label: "GRR / NRR Plans" },
];

// ---------------------------------------------------------------------------
// Connector catalog — 5 keys (§4.1 spec_feature_gate_s665 / locked allowlist)
// ---------------------------------------------------------------------------

const ALL_CONNECTOR_KEYS = [
  { key: "connector.crm.salesforce",  label: "Salesforce CRM" },
  { key: "connector.hris.workday",    label: "Workday HRIS" },
  { key: "connector.hris.mulesoft",   label: "MuleSoft Integration" },
  { key: "connector.crm.generic",     label: "Generic CRM (CSV/API)" },
  { key: "connector.hris.generic",    label: "Generic HRIS (CSV/API)" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function LoadingSkeleton() {
  return (
    <div className="space-y-3">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="h-16 w-full rounded skeleton" />
      ))}
    </div>
  );
}

function ErrorNote({ message }) {
  return (
    <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
      {message ?? "Unable to load tenant detail."}
    </div>
  );
}

function FieldPair({ label, value, mono }) {
  return (
    <div>
      <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">{label}</p>
      <p className={`mt-0.5 text-tok-sm text-[rgb(var(--color-text))] ${mono ? "font-mono" : ""}`}>
        {value ?? <span className="text-[rgb(var(--color-text-muted))]">—</span>}
      </p>
    </div>
  );
}

function SectionHeader({ title }) {
  return (
    <div className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))] mb-3">
      {title}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return iso; }
}

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function utilisationColour(pct, isOverflow) {
  if (isOverflow) return "text-[rgb(var(--color-danger))] font-bold";
  if (pct >= 80)  return "text-[rgb(var(--color-warning))] font-semibold";
  return "text-[rgb(var(--color-success))]";
}

function severityColour(severity) {
  if (severity === "critical") return "text-[rgb(var(--color-danger))]";
  if (severity === "warning")  return "text-[rgb(var(--color-warning))]";
  return "text-[rgb(var(--color-text-muted))]";
}

// ---------------------------------------------------------------------------
// Seat adjustment inline panel
// ---------------------------------------------------------------------------

function SeatAdjustPanel({ tenantId, currentMax, tier, onSaved, onCancel }) {
  const [newMax,  setNewMax]  = useState(String(currentMax ?? ""));
  const [reason,  setReason]  = useState("");
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);

  async function handleSave() {
    setError(null);
    const val = parseInt(newMax, 10);
    if (isNaN(val) || val < 1) {
      setError("New seat count must be a positive integer.");
      return;
    }
    if (!reason.trim()) {
      setError("Reason is required.");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/admin/tenants/${tenantId}/seats`, {
        method:      "PATCH",
        headers:     { "Content-Type": "application/json" },
        credentials: "include",
        body:        JSON.stringify({ new_max_positions: val, reason: reason.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.detail?.error?.message ?? `Error ${res.status}`);
      }
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-3 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 space-y-3">
      <p className="text-tok-xs font-semibold text-[rgb(var(--color-text))]">Adjust Seats</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <label className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
            New seat count
          </label>
          <input
            type="number"
            value={newMax}
            onChange={(e) => setNewMax(e.target.value)}
            disabled={saving}
            placeholder={String(currentMax ?? "")}
            className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-primary))] disabled:opacity-50"
          />
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            Tier: <span className="capitalize">{tier}</span> · Current: {currentMax ?? "—"}
          </p>
        </div>
        <div className="space-y-1">
          <label className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
            Reason <span className="text-[rgb(var(--color-danger))]">*</span>
          </label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={saving}
            placeholder="e.g. Contract amendment — added 25 seats"
            className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-primary))] disabled:opacity-50"
          />
        </div>
      </div>
      {error && <ErrorNote message={error} />}
      <div className="flex items-center gap-2">
        <Button onClick={handleSave} disabled={saving}>
          <Check className="h-3.5 w-3.5" />
          {saving ? "Saving…" : "Save"}
        </Button>
        <Button variant="secondary" onClick={onCancel} disabled={saving}>
          <X className="h-3.5 w-3.5" /> Cancel
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Plan bundle edit inline panel
// ---------------------------------------------------------------------------

function PlanBundleEditPanel({ tenantId, currentKeys, currentEndDate, onSaved, onCancel, isNew = false }) {
  const [selectedKeys, setSelectedKeys] = useState(new Set(currentKeys ?? []));
  const [endDate,      setEndDate]      = useState(currentEndDate ? currentEndDate.slice(0, 10) : "");
  const [reason,       setReason]       = useState("");
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState(null);

  function toggleKey(key) {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  function selectAll()   { setSelectedKeys(new Set(ALL_PLAN_KEYS.map((p) => p.key))); }
  function deselectAll() { setSelectedKeys(new Set()); }

  async function handleSave() {
    setError(null);
    if (!reason.trim()) {
      setError("Reason is required.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        plan_keys:         Array.from(selectedKeys),
        assignment_reason: reason.trim(),
        end_date:          endDate || null,
      };
      const res = await fetch(`${API_BASE}/admin/tenants/${tenantId}/plan-bundle`, {
        method:      isNew ? "POST" : "PATCH",
        headers:     { "Content-Type": "application/json" },
        credentials: "include",
        body:        JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.detail?.error?.message ?? `Error ${res.status}`);
      }
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-3 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 space-y-4">
      <p className="text-tok-xs font-semibold text-[rgb(var(--color-text))]">Edit Plan Bundle</p>

      {/* Select / deselect all */}
      <div className="flex items-center gap-3">
        <button
          onClick={selectAll}
          disabled={saving}
          className="text-tok-xs font-medium text-[rgb(var(--color-primary))] hover:underline disabled:opacity-50"
        >
          Select all
        </button>
        <span className="text-[rgb(var(--color-border))]">·</span>
        <button
          onClick={deselectAll}
          disabled={saving}
          className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:underline disabled:opacity-50"
        >
          Deselect all
        </button>
        <span className="ml-auto text-tok-xs text-[rgb(var(--color-text-muted))]">
          {selectedKeys.size} / {ALL_PLAN_KEYS.length} selected
        </span>
      </div>

      {/* Plan key checklist */}
      <div className="grid gap-1.5 sm:grid-cols-2">
        {ALL_PLAN_KEYS.map(({ key, label }) => (
          <label
            key={key}
            className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-[rgb(var(--color-surface))] cursor-pointer"
          >
            <input
              type="checkbox"
              checked={selectedKeys.has(key)}
              onChange={() => toggleKey(key)}
              disabled={saving}
              className="rounded border-[rgb(var(--color-border))] text-[rgb(var(--color-primary))] focus:ring-[rgb(var(--color-primary))]"
            />
            <span className="text-tok-xs text-[rgb(var(--color-text))]">{label}</span>
          </label>
        ))}
      </div>

      {/* End date + reason */}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <label className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
            Bundle end date <span className="font-normal">(leave blank = no expiry)</span>
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            disabled={saving}
            className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-primary))] disabled:opacity-50"
          />
        </div>
        <div className="space-y-1">
          <label className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
            Reason <span className="text-[rgb(var(--color-danger))]">*</span>
          </label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={saving}
            placeholder="e.g. Contract amendment — added pharma plan"
            className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-primary))] disabled:opacity-50"
          />
        </div>
      </div>

      {error && <ErrorNote message={error} />}
      <div className="flex items-center gap-2">
        <Button onClick={handleSave} disabled={saving}>
          <Check className="h-3.5 w-3.5" />
          {saving ? "Saving…" : "Save"}
        </Button>
        <Button variant="secondary" onClick={onCancel} disabled={saving}>
          <X className="h-3.5 w-3.5" /> Cancel
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Connector edit inline panel
// ---------------------------------------------------------------------------

function ConnectorEditPanel({ tenantId, currentKeys, currentEndDate, onSaved, onCancel, isNew = false }) {
  const [selectedKeys, setSelectedKeys] = useState(new Set(currentKeys ?? []));
  const [endDate,      setEndDate]      = useState(currentEndDate ? currentEndDate.slice(0, 10) : "");
  const [reason,       setReason]       = useState("");
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState(null);

  function toggleKey(key) {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  async function handleSave() {
    setError(null);
    if (!reason.trim()) {
      setError("Reason is required.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        connector_keys:    Array.from(selectedKeys),
        assignment_reason: reason.trim(),
        end_date:          endDate || null,
      };
      const res = await fetch(`${API_BASE}/admin/tenants/${tenantId}/connectors`, {
        method:      isNew ? "POST" : "PATCH",
        headers:     { "Content-Type": "application/json" },
        credentials: "include",
        body:        JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.detail?.error?.message ?? `Error ${res.status}`);
      }
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-3 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 space-y-4">
      <p className="text-tok-xs font-semibold text-[rgb(var(--color-text))]">Edit Connectors</p>

      {/* Connector checklist */}
      <div className="space-y-1.5">
        {ALL_CONNECTOR_KEYS.map(({ key, label }) => (
          <label
            key={key}
            className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-[rgb(var(--color-surface))] cursor-pointer"
          >
            <input
              type="checkbox"
              checked={selectedKeys.has(key)}
              onChange={() => toggleKey(key)}
              disabled={saving}
              className="rounded border-[rgb(var(--color-border))] text-[rgb(var(--color-primary))] focus:ring-[rgb(var(--color-primary))]"
            />
            <span className="text-tok-xs text-[rgb(var(--color-text))]">{label}</span>
            <span className="ml-auto text-tok-xs font-mono text-[rgb(var(--color-text-muted))]">{key}</span>
          </label>
        ))}
      </div>

      {/* End date + reason */}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <label className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
            Bundle end date <span className="font-normal">(leave blank = no expiry)</span>
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            disabled={saving}
            className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-primary))] disabled:opacity-50"
          />
        </div>
        <div className="space-y-1">
          <label className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
            Reason <span className="text-[rgb(var(--color-danger))]">*</span>
          </label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={saving}
            placeholder="e.g. Contract amendment — added Salesforce connector"
            className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-primary))] disabled:opacity-50"
          />
        </div>
      </div>

      {error && <ErrorNote message={error} />}
      <div className="flex items-center gap-2">
        <Button onClick={handleSave} disabled={saving}>
          <Check className="h-3.5 w-3.5" />
          {saving ? "Saving…" : "Save"}
        </Button>
        <Button variant="secondary" onClick={onCancel} disabled={saving}>
          <X className="h-3.5 w-3.5" /> Cancel
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Contract create inline panel
// ---------------------------------------------------------------------------

function ContractCreatePanel({ tenantId, onSaved, onCancel }) {
  const [form,   setForm]   = useState({
    contract_ref:             "",
    contracted_min_positions: "",
    contracted_max_positions: "",
    price_per_seat_usd:       "",
    billing_frequency:        "monthly",
    effective_date:           "",
    expiry_date:              "",
    contract_status:          "draft",
    discount_annual_pct:      "",
    discount_volume_pct:      "",
    discount_pilot_pct:       "",
    discount_nonprofit_pct:   "",
    discount_custom_pct:      "",
    discount_custom_reason:   "",
    msa_reference:            "",
    dpa_reference:            "",
    po_number:                "",
    order_form_ref:           "",
    deal_notes:               "",
    is_custom:                false,
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  function set(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSave() {
    setError(null);
    if (!form.contract_ref.trim()) { setError("Contract reference is required."); return; }
    if (!form.contracted_min_positions || parseInt(form.contracted_min_positions, 10) < 1) {
      setError("Minimum positions must be ≥ 1."); return;
    }
    if (!form.price_per_seat_usd || parseFloat(form.price_per_seat_usd) < 0) {
      setError("Price per seat is required."); return;
    }
    if (!form.effective_date) { setError("Effective date is required."); return; }

    setSaving(true);
    try {
      const body = {
        contract_ref:             form.contract_ref.trim(),
        contracted_min_positions: parseInt(form.contracted_min_positions, 10),
        contracted_max_positions: form.contracted_max_positions ? parseInt(form.contracted_max_positions, 10) : null,
        price_per_seat_usd:       parseFloat(form.price_per_seat_usd),
        billing_frequency:        form.billing_frequency,
        effective_date:           form.effective_date,
        expiry_date:              form.expiry_date || null,
        contract_status:          form.contract_status,
        discount_annual_pct:      form.discount_annual_pct      ? parseFloat(form.discount_annual_pct)     : 0,
        discount_volume_pct:      form.discount_volume_pct      ? parseFloat(form.discount_volume_pct)     : 0,
        discount_pilot_pct:       form.discount_pilot_pct       ? parseFloat(form.discount_pilot_pct)      : 0,
        discount_nonprofit_pct:   form.discount_nonprofit_pct   ? parseFloat(form.discount_nonprofit_pct)  : 0,
        discount_custom_pct:      form.discount_custom_pct      ? parseFloat(form.discount_custom_pct)     : 0,
        discount_custom_reason:   form.discount_custom_reason.trim() || null,
        msa_reference:            form.msa_reference.trim()   || null,
        dpa_reference:            form.dpa_reference.trim()   || null,
        po_number:                form.po_number.trim()       || null,
        order_form_ref:           form.order_form_ref.trim()  || null,
        deal_notes:               form.deal_notes.trim()      || null,
        is_custom:                form.is_custom,
      };
      const res = await fetch(`${API_BASE}/admin/tenants/${tenantId}/contracts`, {
        method:      "POST",
        headers:     { "Content-Type": "application/json" },
        credentials: "include",
        body:        JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail?.error?.message ?? `Error ${res.status}`);
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  const inputCls = "w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-primary))] disabled:opacity-50";
  const labelCls = "text-tok-xs font-medium text-[rgb(var(--color-text-muted))]";

  return (
    <div className="mt-3 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 space-y-4">
      <p className="text-tok-xs font-semibold text-[rgb(var(--color-text))]">Create Contract</p>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <label className={labelCls}>Contract ref <span className="text-[rgb(var(--color-danger))]">*</span></label>
          <input type="text" value={form.contract_ref} onChange={(e) => set("contract_ref", e.target.value)} disabled={saving} placeholder="e.g. ORDER-2026-001" className={inputCls} />
        </div>
        <div className="space-y-1">
          <label className={labelCls}>Status</label>
          <select value={form.contract_status} onChange={(e) => set("contract_status", e.target.value)} disabled={saving} className={inputCls}>
            {["draft", "active", "expired", "terminated", "renewed"].map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className={labelCls}>Min positions <span className="text-[rgb(var(--color-danger))]">*</span></label>
          <input type="number" min="1" value={form.contracted_min_positions} onChange={(e) => set("contracted_min_positions", e.target.value)} disabled={saving} className={inputCls} />
        </div>
        <div className="space-y-1">
          <label className={labelCls}>Max positions <span className="font-normal">(leave blank = unlimited)</span></label>
          <input type="number" min="1" value={form.contracted_max_positions} onChange={(e) => set("contracted_max_positions", e.target.value)} disabled={saving} className={inputCls} />
        </div>
        <div className="space-y-1">
          <label className={labelCls}>Price / seat / month (USD) <span className="text-[rgb(var(--color-danger))]">*</span></label>
          <input type="number" min="0" step="0.01" value={form.price_per_seat_usd} onChange={(e) => set("price_per_seat_usd", e.target.value)} disabled={saving} className={inputCls} />
        </div>
        <div className="space-y-1">
          <label className={labelCls}>Billing frequency</label>
          <select value={form.billing_frequency} onChange={(e) => set("billing_frequency", e.target.value)} disabled={saving} className={inputCls}>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="annual">Annual</option>
          </select>
        </div>
        <div className="space-y-1">
          <label className={labelCls}>Effective date <span className="text-[rgb(var(--color-danger))]">*</span></label>
          <input type="date" value={form.effective_date} onChange={(e) => set("effective_date", e.target.value)} disabled={saving} className={inputCls} />
        </div>
        <div className="space-y-1">
          <label className={labelCls}>Expiry date <span className="font-normal">(leave blank = no expiry)</span></label>
          <input type="date" value={form.expiry_date} onChange={(e) => set("expiry_date", e.target.value)} disabled={saving} className={inputCls} />
        </div>
      </div>

      {/* Discount stack */}
      <div>
        <p className={`${labelCls} mb-2`}>Discount stack <span className="font-normal">(total ≤ 40%)</span></p>
        <div className="grid gap-2 sm:grid-cols-3">
          {[
            ["Annual %",    "discount_annual_pct"],
            ["Volume %",    "discount_volume_pct"],
            ["Pilot %",     "discount_pilot_pct"],
            ["Nonprofit %", "discount_nonprofit_pct"],
            ["Custom %",    "discount_custom_pct"],
          ].map(([lbl, field]) => (
            <div key={field} className="space-y-1">
              <label className={labelCls}>{lbl}</label>
              <input type="number" min="0" max="40" step="0.01" value={form[field]} onChange={(e) => set(field, e.target.value)} disabled={saving} className={inputCls} />
            </div>
          ))}
          <div className="space-y-1 sm:col-span-3">
            <label className={labelCls}>Custom discount reason</label>
            <input type="text" value={form.discount_custom_reason} onChange={(e) => set("discount_custom_reason", e.target.value)} disabled={saving} placeholder="e.g. Strategic partnership" className={inputCls} />
          </div>
        </div>
      </div>

      {/* Commercial refs */}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <label className={labelCls}>MSA reference</label>
          <input type="text" value={form.msa_reference} onChange={(e) => set("msa_reference", e.target.value)} disabled={saving} className={inputCls} />
        </div>
        <div className="space-y-1">
          <label className={labelCls}>DPA reference</label>
          <input type="text" value={form.dpa_reference} onChange={(e) => set("dpa_reference", e.target.value)} disabled={saving} className={inputCls} />
        </div>
        <div className="space-y-1">
          <label className={labelCls}>PO number</label>
          <input type="text" value={form.po_number} onChange={(e) => set("po_number", e.target.value)} disabled={saving} className={inputCls} />
        </div>
        <div className="space-y-1">
          <label className={labelCls}>Order form ref</label>
          <input type="text" value={form.order_form_ref} onChange={(e) => set("order_form_ref", e.target.value)} disabled={saving} className={inputCls} />
        </div>
        <div className="space-y-1 sm:col-span-2">
          <label className={labelCls}>Deal notes</label>
          <textarea value={form.deal_notes} onChange={(e) => set("deal_notes", e.target.value)} disabled={saving} rows={2} className={inputCls} />
        </div>
      </div>

      {/* is_custom flag */}
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={form.is_custom} onChange={(e) => set("is_custom", e.target.checked)} disabled={saving} className="rounded border-[rgb(var(--color-border))] text-[rgb(var(--color-primary))]" />
        <span className="text-tok-xs text-[rgb(var(--color-text))]">Custom pricing (appends -CUSTOM to SKU)</span>
      </label>

      {error && <ErrorNote message={error} />}
      <div className="flex items-center gap-2">
        <Button onClick={handleSave} disabled={saving}>
          <Check className="h-3.5 w-3.5" />
          {saving ? "Creating…" : "Create contract"}
        </Button>
        <Button variant="secondary" onClick={onCancel} disabled={saving}>
          <X className="h-3.5 w-3.5" /> Cancel
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Contract edit inline panel (PATCH — partial update)
// ---------------------------------------------------------------------------

function ContractEditPanel({ tenantId, contract, onSaved, onCancel }) {
  const [form,   setForm]   = useState({
    contract_status:  contract.contract_status  ?? "draft",
    price_per_seat_usd: String(contract.price_per_seat_usd ?? ""),
    billing_frequency:  contract.billing_frequency ?? "monthly",
    expiry_date:        contract.expiry_date ? contract.expiry_date.slice(0, 10) : "",
    msa_reference:      contract.msa_reference   ?? "",
    dpa_reference:      contract.dpa_reference   ?? "",
    po_number:          contract.po_number        ?? "",
    deal_notes:         contract.deal_notes       ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  function set(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSave() {
    setError(null);
    setSaving(true);
    try {
      const body = {
        contract_status:    form.contract_status,
        price_per_seat_usd: form.price_per_seat_usd ? parseFloat(form.price_per_seat_usd) : undefined,
        billing_frequency:  form.billing_frequency,
        expiry_date:        form.expiry_date || null,
        msa_reference:      form.msa_reference.trim() || null,
        dpa_reference:      form.dpa_reference.trim() || null,
        po_number:          form.po_number.trim()     || null,
        deal_notes:         form.deal_notes.trim()    || null,
      };
      // Remove undefined fields — PATCH only sends what changed
      Object.keys(body).forEach((k) => body[k] === undefined && delete body[k]);

      const res = await fetch(`${API_BASE}/admin/tenants/${tenantId}/contracts/${contract.contract_id}`, {
        method:      "PATCH",
        headers:     { "Content-Type": "application/json" },
        credentials: "include",
        body:        JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail?.error?.message ?? `Error ${res.status}`);
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  const inputCls = "w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-primary))] disabled:opacity-50";
  const labelCls = "text-tok-xs font-medium text-[rgb(var(--color-text-muted))]";

  return (
    <div className="mt-3 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 space-y-4">
      <p className="text-tok-xs font-semibold text-[rgb(var(--color-text))]">Edit Contract</p>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <label className={labelCls}>Status</label>
          <select value={form.contract_status} onChange={(e) => set("contract_status", e.target.value)} disabled={saving} className={inputCls}>
            {["draft", "active", "expired", "terminated", "renewed"].map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className={labelCls}>Price / seat / month (USD)</label>
          <input type="number" min="0" step="0.01" value={form.price_per_seat_usd} onChange={(e) => set("price_per_seat_usd", e.target.value)} disabled={saving} className={inputCls} />
        </div>
        <div className="space-y-1">
          <label className={labelCls}>Billing frequency</label>
          <select value={form.billing_frequency} onChange={(e) => set("billing_frequency", e.target.value)} disabled={saving} className={inputCls}>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="annual">Annual</option>
          </select>
        </div>
        <div className="space-y-1">
          <label className={labelCls}>Expiry date <span className="font-normal">(leave blank = no expiry)</span></label>
          <input type="date" value={form.expiry_date} onChange={(e) => set("expiry_date", e.target.value)} disabled={saving} className={inputCls} />
        </div>
        <div className="space-y-1">
          <label className={labelCls}>MSA reference</label>
          <input type="text" value={form.msa_reference} onChange={(e) => set("msa_reference", e.target.value)} disabled={saving} className={inputCls} />
        </div>
        <div className="space-y-1">
          <label className={labelCls}>DPA reference</label>
          <input type="text" value={form.dpa_reference} onChange={(e) => set("dpa_reference", e.target.value)} disabled={saving} className={inputCls} />
        </div>
        <div className="space-y-1">
          <label className={labelCls}>PO number</label>
          <input type="text" value={form.po_number} onChange={(e) => set("po_number", e.target.value)} disabled={saving} className={inputCls} />
        </div>
        <div className="space-y-1">
          <label className={labelCls}>Deal notes</label>
          <textarea value={form.deal_notes} onChange={(e) => set("deal_notes", e.target.value)} disabled={saving} rows={2} className={inputCls} />
        </div>
      </div>

      {error && <ErrorNote message={error} />}
      <div className="flex items-center gap-2">
        <Button onClick={handleSave} disabled={saving}>
          <Check className="h-3.5 w-3.5" />
          {saving ? "Saving…" : "Save changes"}
        </Button>
        <Button variant="secondary" onClick={onCancel} disabled={saving}>
          <X className="h-3.5 w-3.5" /> Cancel
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TenantDetail
// ---------------------------------------------------------------------------

export default function TenantDetail() {
  const { tenantId } = useParams();
  const [tenant,    setTenant]    = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [adjusting, setAdjusting] = useState(false);

  // Plan bundle state
  const [planBundle,        setPlanBundle]        = useState(null);   // null = not yet loaded
  const [planBundleLoading, setPlanBundleLoading] = useState(true);
  const [planBundleError,   setPlanBundleError]   = useState(null);
  const [planBundleNone,    setPlanBundleNone]    = useState(false);  // true = 404 = short-circuit ALLOW
  const [editingBundle,     setEditingBundle]     = useState(false);

  // Connector state
  const [connectors,        setConnectors]        = useState(null);
  const [connectorsLoading, setConnectorsLoading] = useState(true);
  const [connectorsError,   setConnectorsError]   = useState(null);
  const [connectorsNone,    setConnectorsNone]    = useState(false);
  const [editingConnectors, setEditingConnectors] = useState(false);

  // Contract state
  const [contract,        setContract]        = useState(null);   // most recent contract or null
  const [contractLoading, setContractLoading] = useState(true);
  const [contractError,   setContractError]   = useState(null);
  const [contractNone,    setContractNone]    = useState(false);  // true = no contracts exist yet
  const [editingContract, setEditingContract] = useState(false);
  const [creatingContract, setCreatingContract] = useState(false);
  // Order form download state
  const [orderFormLoading, setOrderFormLoading] = useState(false);
  const [orderFormError,   setOrderFormError]   = useState(null);

  const fetchTenant = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/tenants/${tenantId}`, {
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail?.error?.message ?? `Error ${res.status}`);
      }
      const data = await res.json();
      setTenant(data.tenant ?? data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  const fetchPlanBundle = useCallback(async () => {
    if (!tenantId) return;
    setPlanBundleLoading(true);
    setPlanBundleError(null);
    setPlanBundleNone(false);
    try {
      const res = await fetch(`${API_BASE}/admin/tenants/${tenantId}/plan-bundle`, {
        credentials: "include",
      });
      if (res.status === 404) {
        setPlanBundleNone(true);
        setPlanBundle(null);
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail?.error?.message ?? `Error ${res.status}`);
      }
      const data = await res.json();
      setPlanBundle(data);
    } catch (e) {
      setPlanBundleError(e.message);
    } finally {
      setPlanBundleLoading(false);
    }
  }, [tenantId]);

  const fetchConnectors = useCallback(async () => {
    if (!tenantId) return;
    setConnectorsLoading(true);
    setConnectorsError(null);
    setConnectorsNone(false);
    try {
      const res = await fetch(`${API_BASE}/admin/tenants/${tenantId}/connectors`, {
        credentials: "include",
      });
      if (res.status === 404) {
        setConnectorsNone(true);
        setConnectors(null);
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail?.error?.message ?? `Error ${res.status}`);
      }
      const data = await res.json();
      setConnectors(data);
    } catch (e) {
      setConnectorsError(e.message);
    } finally {
      setConnectorsLoading(false);
    }
  }, [tenantId]);

  const fetchContract = useCallback(async () => {
    if (!tenantId) return;
    setContractLoading(true);
    setContractError(null);
    setContractNone(false);
    try {
      const res = await fetch(`${API_BASE}/admin/tenants/${tenantId}/contracts`, {
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail?.error?.message ?? `Error ${res.status}`);
      }
      const data = await res.json();
      if (!data.contracts || data.contracts.length === 0) {
        setContractNone(true);
        setContract(null);
      } else {
        setContract(data.contracts[0]); // most recent first
      }
    } catch (e) {
      setContractError(e.message);
    } finally {
      setContractLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { fetchTenant(); },    [fetchTenant]);
  useEffect(() => { fetchPlanBundle(); }, [fetchPlanBundle]);
  useEffect(() => { fetchConnectors(); }, [fetchConnectors]);
  useEffect(() => { fetchContract(); },  [fetchContract]);

  function handleAdjustSaved() {
    setAdjusting(false);
    fetchTenant();
  }

  function handleBundleSaved() {
    setEditingBundle(false);
    fetchPlanBundle();
  }

  function handleConnectorsSaved() {
    setEditingConnectors(false);
    fetchConnectors();
  }

  function handleContractSaved() {
    setCreatingContract(false);
    setEditingContract(false);
    fetchContract();
  }

  async function downloadOrderForm() {
    if (!contract?.contract_id) return;
    setOrderFormLoading(true);
    setOrderFormError(null);
    try {
      const res = await fetch(
        `${API_BASE}/admin/tenants/${tenantId}/contracts/${contract.contract_id}/order-form`,
        { credentials: "include" }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.detail?.error?.message ?? `Error ${res.status}`);
      }
      // Open presigned URL in new tab — browser handles the download
      window.open(data.presigned_url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setOrderFormError(e.message);
    } finally {
      setOrderFormLoading(false);
    }
  }

  const active    = tenant?.active_position_count ?? 0;
  const purchased = tenant?.max_positions ?? null;
  const pct       = tenant?.utilisation_pct ?? (purchased ? Math.round((active / purchased) * 100) : 0);
  const isOverflow = purchased != null && active > purchased;

  return (
    <div className="space-y-4 max-w-4xl">

      {/* Back link */}
      <Link
        to="/platform-admin/tenants"
        className="inline-flex items-center gap-1.5 text-tok-xs text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] transition"
      >
        <ChevronLeft className="h-3.5 w-3.5" /> All tenants
      </Link>

      {loading && <LoadingSkeleton />}
      {error   && <ErrorNote message={error} />}

      {!loading && !error && tenant && (
        <>
          {/* ── Identity ── */}
          <Card
            title={tenant.tenant_name}
            action={
              <Button variant="secondary" onClick={fetchTenant} disabled={loading}>
                <RefreshCw className="h-4 w-4" /> Refresh
              </Button>
            }
          >
            <SectionHeader title="Identity" />
            <div className="grid gap-3 sm:grid-cols-3">
              <FieldPair label="Tenant ID"    value={tenant.tenant_id}        mono />
              <FieldPair label="Slug"          value={tenant.tenant_slug}      mono />
              <FieldPair label="Status"        value={tenant.tenant_status_code} />
              <FieldPair label="Tier"          value={tenant.subscription_tier} />
              <FieldPair label="Country"       value={tenant.hq_country_code} />
              <FieldPair label="Currency"      value={tenant.reporting_currency_code} />
              <FieldPair label="Type"          value={tenant.is_demo ? "Demo" : "Live"} />
              <FieldPair label="Legal entity"  value={tenant.legal_entity_name} />
              <FieldPair label="Billing email" value={tenant.billing_email} />
            </div>

            {/* Compliance row */}
            {(tenant.msa_signed_at || tenant.dpa_signed_at || tenant.ein || tenant.po_number) && (
              <>
                <div className="my-4 border-t border-[rgb(var(--color-border))]" />
                <SectionHeader title="Compliance" />
                <div className="grid gap-3 sm:grid-cols-3">
                  <FieldPair label="MSA signed"    value={formatDate(tenant.msa_signed_at)} />
                  <FieldPair label="MSA reference" value={tenant.msa_reference} />
                  <FieldPair label="DPA signed"    value={formatDate(tenant.dpa_signed_at)} />
                  <FieldPair label="DPA reference" value={tenant.dpa_reference} />
                  <FieldPair label="EIN"           value={tenant.ein} mono />
                  <FieldPair label="PO number"     value={tenant.po_number} mono />
                </div>
              </>
            )}
          </Card>

          {/* ── Seat Position ── */}
          <Card title="Seat Position">
            <div className="grid gap-4 sm:grid-cols-4">
              <div>
                <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Active positions</p>
                <p className={`mt-0.5 text-tok-xl font-bold ${utilisationColour(pct, isOverflow)}`}>
                  {active}
                </p>
              </div>
              <div>
                <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Purchased seats</p>
                <p className="mt-0.5 text-tok-xl font-bold text-[rgb(var(--color-text))]">
                  {purchased ?? "—"}
                </p>
              </div>
              <div>
                <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Utilisation</p>
                <p className={`mt-0.5 text-tok-xl font-bold ${utilisationColour(pct, isOverflow)}`}>
                  {purchased ? `${pct.toFixed(1)}%` : "—"}
                </p>
              </div>
              <div>
                <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Status</p>
                <p className={`mt-0.5 text-tok-sm font-semibold ${utilisationColour(pct, isOverflow)}`}>
                  {isOverflow
                    ? "⚠ Overflow"
                    : pct >= 80
                    ? "Near limit"
                    : "Healthy"
                  }
                </p>
              </div>
            </div>

            {/* Utilisation bar */}
            {purchased != null && (
              <div className="mt-3">
                <div className="h-2 w-full rounded-full bg-[rgb(var(--color-surface-muted))]">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      isOverflow ? "bg-[rgb(var(--color-danger))]"
                      : pct >= 80  ? "bg-[rgb(var(--color-warning))]"
                      : "bg-[rgb(var(--color-success))]"
                    }`}
                    style={{ width: `${Math.min(pct, 100)}%` }}
                  />
                </div>
                <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
                  {active} of {purchased} seats used
                  {isOverflow && (
                    <span className="ml-2 text-[rgb(var(--color-danger))] font-medium">
                      · {active - purchased} over limit — overflow positions are inactive
                    </span>
                  )}
                </p>
              </div>
            )}

            {/* Adjust seats */}
            {!adjusting && (
              <div className="mt-4">
                <button
                  onClick={() => setAdjusting(true)}
                  className="flex items-center gap-1.5 rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] transition"
                >
                  <Edit2 className="h-3.5 w-3.5" /> Adjust Seats
                </button>
              </div>
            )}

            {adjusting && (
              <SeatAdjustPanel
                tenantId={tenantId}
                currentMax={purchased}
                tier={tenant.subscription_tier}
                onSaved={handleAdjustSaved}
                onCancel={() => setAdjusting(false)}
              />
            )}
          </Card>

          {/* ── Plan Bundle ── */}
          <Card title="Plan Bundle">
            {planBundleLoading && (
              <div className="h-10 w-full rounded skeleton" />
            )}

            {planBundleError && (
              <ErrorNote message={planBundleError} />
            )}

            {!planBundleLoading && !planBundleError && planBundleNone && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2.5">
                  <Package className="h-4 w-4 shrink-0 text-[rgb(var(--color-text-muted))]" />
                  <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                    No bundle assigned — all 28 plan types open (default).
                  </p>
                </div>
                {!editingBundle && (
                  <button
                    onClick={() => setEditingBundle(true)}
                    className="flex items-center gap-1.5 rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] transition"
                  >
                    <Edit2 className="h-3.5 w-3.5" /> Assign bundle
                  </button>
                )}
              </div>
            )}

            {!planBundleLoading && !planBundleError && planBundle && (
              <div className="space-y-3">
                {/* Bundle metadata */}
                <div className="grid gap-3 sm:grid-cols-3">
                  <FieldPair label="Bundle name"       value={planBundle.bundle_name}        mono />
                  <FieldPair label="End date"          value={formatDate(planBundle.end_date)} />
                  <FieldPair label="Assignment reason" value={planBundle.assignment_reason} />
                </div>

                {/* Plan key tags */}
                <div>
                  <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))] mb-2">
                    Plan types ({planBundle.plan_keys?.length ?? 0} / {ALL_PLAN_KEYS.length})
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {ALL_PLAN_KEYS.map(({ key, label }) => {
                      const active = planBundle.plan_keys?.includes(key);
                      return (
                        <span
                          key={key}
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-tok-xs font-medium ${
                            active
                              ? "bg-[rgb(var(--color-success)_/_0.12)] text-[rgb(var(--color-success))]"
                              : "bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))] line-through"
                          }`}
                        >
                          {label}
                        </span>
                      );
                    })}
                  </div>
                </div>

                {!editingBundle && (
                  <button
                    onClick={() => setEditingBundle(true)}
                    className="flex items-center gap-1.5 rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] transition"
                  >
                    <Edit2 className="h-3.5 w-3.5" /> Edit bundle
                  </button>
                )}
              </div>
            )}

            {editingBundle && (
              <PlanBundleEditPanel
                tenantId={tenantId}
                currentKeys={planBundle?.plan_keys ?? ALL_PLAN_KEYS.map((p) => p.key)}
                currentEndDate={planBundle?.end_date ?? null}
                onSaved={handleBundleSaved}
                onCancel={() => setEditingBundle(false)}
                isNew={planBundleNone}
              />
            )}
          </Card>

          {/* ── Connectors ── */}
          <Card title="Connectors">
            {connectorsLoading && (
              <div className="h-10 w-full rounded skeleton" />
            )}

            {connectorsError && (
              <ErrorNote message={connectorsError} />
            )}

            {!connectorsLoading && !connectorsError && connectorsNone && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2.5">
                  <Plug className="h-4 w-4 shrink-0 text-[rgb(var(--color-text-muted))]" />
                  <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                    No connector bundle assigned.
                  </p>
                </div>
                {!editingConnectors && (
                  <button
                    onClick={() => setEditingConnectors(true)}
                    className="flex items-center gap-1.5 rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] transition"
                  >
                    <Edit2 className="h-3.5 w-3.5" /> Assign connectors
                  </button>
                )}
              </div>
            )}

            {!connectorsLoading && !connectorsError && connectors && (
              <div className="space-y-3">
                {/* Connector bundle metadata */}
                <div className="grid gap-3 sm:grid-cols-3">
                  <FieldPair label="Bundle name"       value={connectors.bundle_name}        mono />
                  <FieldPair label="End date"          value={formatDate(connectors.end_date)} />
                  <FieldPair label="Assignment reason" value={connectors.assignment_reason} />
                </div>

                {/* Connector key rows */}
                <div>
                  <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))] mb-2">
                    Connectors ({connectors.connector_keys?.length ?? 0} / {ALL_CONNECTOR_KEYS.length})
                  </p>
                  <div className="space-y-1.5">
                    {ALL_CONNECTOR_KEYS.map(({ key, label }) => {
                      const active = connectors.connector_keys?.includes(key);
                      return (
                        <div
                          key={key}
                          className={`flex items-center justify-between rounded-lg px-3 py-2 ${
                            active
                              ? "bg-[rgb(var(--color-success)_/_0.08)]"
                              : "bg-[rgb(var(--color-surface-muted))]"
                          }`}
                        >
                          <span className={`text-tok-xs font-medium ${
                            active
                              ? "text-[rgb(var(--color-text))]"
                              : "text-[rgb(var(--color-text-muted))] line-through"
                          }`}>
                            {label}
                          </span>
                          <span className="text-tok-xs font-mono text-[rgb(var(--color-text-muted))]">
                            {key}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {!editingConnectors && (
                  <button
                    onClick={() => setEditingConnectors(true)}
                    className="flex items-center gap-1.5 rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] transition"
                  >
                    <Edit2 className="h-3.5 w-3.5" /> Edit connectors
                  </button>
                )}
              </div>
            )}

            {editingConnectors && (
              <ConnectorEditPanel
                tenantId={tenantId}
                currentKeys={connectors?.connector_keys ?? []}
                currentEndDate={connectors?.end_date ?? null}
                onSaved={handleConnectorsSaved}
                onCancel={() => setEditingConnectors(false)}
                isNew={connectorsNone}
              />
            )}
          </Card>

          {/* ── Contract ── */}
          <Card title="Contract">
            {contractLoading && (
              <div className="h-10 w-full rounded skeleton" />
            )}

            {contractError && (
              <ErrorNote message={contractError} />
            )}

            {!contractLoading && !contractError && contractNone && (
              <div className="space-y-3">
                {/* Warning badge for live tenants — demo tenants don't require a contract */}
                {!tenant.is_demo ? (
                  <div className="flex items-center gap-2 rounded-xl border border-[rgb(var(--color-warning)_/_0.4)] bg-[rgb(var(--color-warning)_/_0.08)] px-3 py-2.5">
                    <AlertTriangle className="h-4 w-4 shrink-0 text-[rgb(var(--color-warning))]" />
                    <div>
                      <p className="text-tok-sm font-medium text-[rgb(var(--color-warning))]">
                        No contract on file
                      </p>
                      <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">
                        This is a live tenant. Create a contract to record SKU, pricing, and commercial terms before activation.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2.5">
                    <FileText className="h-4 w-4 shrink-0 text-[rgb(var(--color-text-muted))]" />
                    <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                      No contract on file. Create one to record SKU, pricing, and commercial terms.
                    </p>
                  </div>
                )}
                {!creatingContract && (
                  <button
                    onClick={() => setCreatingContract(true)}
                    className="flex items-center gap-1.5 rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] transition"
                  >
                    <Edit2 className="h-3.5 w-3.5" /> Create contract
                  </button>
                )}
              </div>
            )}

            {!contractLoading && !contractError && contract && !editingContract && (
              <div className="space-y-3">
                {/* SKU + status row */}
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="rounded-full bg-[rgb(var(--color-primary)_/_0.1)] px-3 py-0.5 text-tok-xs font-mono font-semibold text-[rgb(var(--color-primary))]">
                    {contract.sku_id}
                  </span>
                  <span className={`rounded-full px-2.5 py-0.5 text-tok-xs font-medium capitalize ${
                    contract.contract_status === "active"
                      ? "bg-[rgb(var(--color-success)_/_0.12)] text-[rgb(var(--color-success))]"
                      : contract.contract_status === "draft"
                      ? "bg-[rgb(var(--color-warning)_/_0.12)] text-[rgb(var(--color-warning))]"
                      : "bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]"
                  }`}>
                    {contract.contract_status}
                  </span>
                </div>

                {/* Core fields */}
                <div className="grid gap-3 sm:grid-cols-3">
                  <FieldPair label="Contract ref"      value={contract.contract_ref}                        mono />
                  <FieldPair label="Effective date"    value={formatDate(contract.effective_date)} />
                  <FieldPair label="Expiry date"       value={formatDate(contract.expiry_date) ?? "No expiry"} />
                  <FieldPair label="Min positions"     value={contract.contracted_min_positions} />
                  <FieldPair label="Max positions"     value={contract.contracted_max_positions ?? "Unlimited"} />
                  <FieldPair label="Price / seat"      value={contract.price_per_seat_usd != null ? `$${Number(contract.price_per_seat_usd).toFixed(2)}/mo` : null} />
                  <FieldPair label="Billing frequency" value={contract.billing_frequency} />
                  <FieldPair label="Discount total"    value={contract.discount_total_pct != null ? `${contract.discount_total_pct}%` : "0%"} />
                  <FieldPair label="Auto-renews"       value={contract.auto_renews ? "Yes" : "No"} />
                  <FieldPair label="MSA reference"     value={contract.msa_reference}  mono />
                  <FieldPair label="DPA reference"     value={contract.dpa_reference}  mono />
                  <FieldPair label="PO number"         value={contract.po_number}      mono />
                </div>

                {contract.deal_notes && (
                  <div>
                    <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))] mb-1">Deal notes</p>
                    <p className="text-tok-sm text-[rgb(var(--color-text))]">{contract.deal_notes}</p>
                  </div>
                )}

                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => setEditingContract(true)}
                    className="flex items-center gap-1.5 rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] transition"
                  >
                    <Edit2 className="h-3.5 w-3.5" /> Edit contract
                  </button>
                  {contract.order_form_available && (
                    <button
                      onClick={downloadOrderForm}
                      disabled={orderFormLoading}
                      className="flex items-center gap-1.5 rounded-lg border border-[rgb(var(--color-primary)_/_0.4)] bg-[rgb(var(--color-primary)_/_0.06)] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-primary))] hover:bg-[rgb(var(--color-primary)_/_0.12)] disabled:opacity-50 transition"
                    >
                      <FileText className="h-3.5 w-3.5" />
                      {orderFormLoading ? "Generating link…" : "Download order form"}
                    </button>
                  )}
                </div>
                {orderFormError && (
                  <ErrorNote message={orderFormError} />
                )}
              </div>
            )}

            {creatingContract && (
              <ContractCreatePanel
                tenantId={tenantId}
                onSaved={handleContractSaved}
                onCancel={() => setCreatingContract(false)}
              />
            )}

            {editingContract && contract && (
              <ContractEditPanel
                tenantId={tenantId}
                contract={contract}
                onSaved={handleContractSaved}
                onCancel={() => setEditingContract(false)}
              />
            )}
          </Card>

          {/* ── Billing History ── */}
          {tenant.billing_history && tenant.billing_history.length > 0 && (
            <Card title="Billing History">
              <div className="overflow-auto rounded-xl border border-[rgb(var(--color-border))]"
                   style={{ maxHeight: "260px" }}>
                <table className="w-full border-collapse text-tok-sm">
                  <thead className="sticky top-0 z-10 bg-[rgb(var(--color-surface))]">
                    <tr className="border-b border-[rgb(var(--color-border))]">
                      {["Date", "Reason", "Seats before", "Seats after", "Changed by", "Notes"].map((h) => (
                        <th key={h} className="py-2 pr-4 text-left text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))] whitespace-nowrap first:pl-3">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tenant.billing_history.map((row, i) => (
                      <tr key={i} className="border-b border-[rgb(var(--color-border))] last:border-0 hover:bg-[rgb(var(--color-surface-muted))] transition">
                        <td className="py-2.5 pl-3 pr-4 text-tok-xs text-[rgb(var(--color-text-muted))] whitespace-nowrap">
                          {formatDateTime(row.snapshot_at)}
                        </td>
                        <td className="py-2.5 pr-4 text-tok-xs text-[rgb(var(--color-text))] whitespace-nowrap">
                          {row.snapshot_reason ?? "—"}
                        </td>
                        <td className="py-2.5 pr-4 text-tok-xs text-[rgb(var(--color-text-muted))] whitespace-nowrap">
                          {row.previous_max_positions ?? "—"}
                        </td>
                        <td className="py-2.5 pr-4 text-tok-xs font-medium text-[rgb(var(--color-text))] whitespace-nowrap">
                          {row.new_max_positions ?? row.max_positions ?? "—"}
                        </td>
                        <td className="py-2.5 pr-4 text-tok-xs font-mono text-[rgb(var(--color-text-muted))] whitespace-nowrap max-w-[120px] truncate" title={row.changed_by}>
                          {row.changed_by ? row.changed_by.slice(0, 8) + "…" : "—"}
                        </td>
                        <td className="py-2.5 pr-4 text-tok-xs text-[rgb(var(--color-text-muted))] max-w-[200px] truncate" title={row.notes}>
                          {row.notes ?? "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* ── Platform Events ── */}
          {tenant.platform_events && tenant.platform_events.length > 0 && (
            <Card title="Platform Events">
              <div className="overflow-auto rounded-xl border border-[rgb(var(--color-border))]"
                   style={{ maxHeight: "260px" }}>
                <table className="w-full border-collapse text-tok-sm">
                  <thead className="sticky top-0 z-10 bg-[rgb(var(--color-surface))]">
                    <tr className="border-b border-[rgb(var(--color-border))]">
                      {["Date", "Event", "Severity", "Detail"].map((h) => (
                        <th key={h} className="py-2 pr-4 text-left text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))] whitespace-nowrap first:pl-3">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tenant.platform_events.map((evt, i) => (
                      <tr key={i} className="border-b border-[rgb(var(--color-border))] last:border-0 hover:bg-[rgb(var(--color-surface-muted))] transition">
                        <td className="py-2.5 pl-3 pr-4 text-tok-xs text-[rgb(var(--color-text-muted))] whitespace-nowrap">
                          {formatDateTime(evt.created_at)}
                        </td>
                        <td className="py-2.5 pr-4 text-tok-xs font-mono text-[rgb(var(--color-text))] whitespace-nowrap">
                          {evt.event_type ?? "—"}
                        </td>
                        <td className={`py-2.5 pr-4 text-tok-xs font-medium whitespace-nowrap ${severityColour(evt.severity)}`}>
                          {evt.severity ?? "info"}
                          {evt.severity === "warning" && (
                            <AlertTriangle className="inline h-3 w-3 ml-1" />
                          )}
                        </td>
                        <td className="py-2.5 pr-4 text-tok-xs text-[rgb(var(--color-text-muted))] max-w-[280px] truncate"
                            title={typeof evt.detail === "string" ? evt.detail : JSON.stringify(evt.detail)}>
                          {typeof evt.detail === "object"
                            ? JSON.stringify(evt.detail)
                            : (evt.detail ?? "—")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Empty state for events */}
          {(!tenant.platform_events || tenant.platform_events.length === 0) &&
           (!tenant.billing_history || tenant.billing_history.length === 0) && (
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
              No billing history or platform events recorded for this tenant yet.
            </p>
          )}
        </>
      )}
    </div>
  );
}
